import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { Button, ColumnDefinition, Cooltip, Cursor, Icon, Tab, Table, TabPanel } from '@abb/common-ux';
import { NewRequestConfirmationModel } from '@module/analysis/CustomerReport/NewRequestConfirmationModel';
import { IMatchedSpareParts, IRequestReportDetails, RequestReportDetails } from '@module/shared';
import { MySparePartsDonut } from '@module/shared/components/Donut/MySparePartsDonut';
import { Waiting } from '@module/shared/components/Waiting';
import { FetchCustomerReportData } from '@service/CustomerReportsService';
import { DownloadReport } from '@service/DownloadTemplateService';
import { IConnectedComponent } from 'reducers';

export interface IRequestLogDetailState {
  requestDetails: IRequestReportDetails;
  confirmationModelOpen: boolean;
  isContentLoaded: boolean;
  waiting: boolean;
}

class CustomerReportComponent extends React.Component<IConnectedComponent, IRequestLogDetailState> {

  constructor(props: any) {
    super(props);
    this.state = {
      requestDetails: new RequestReportDetails(),
      confirmationModelOpen: false, 
      isContentLoaded: false,
      waiting: false
    };
  }

  async componentDidMount() {
    await this.getCustomerReport();
  }

  getCustomerReport = async () => {
    const dispatch = this.props.dispatch;
    try {
      const requestDetail = await FetchCustomerReportData(this.props.match.params.requestId);
      this.setState({
        requestDetails: requestDetail,
        isContentLoaded: true
      });
    } catch (error) {
      if (error.status === 475) {
        dispatch({ type: 'ADD_BANNER', banners: [{id: 'getRequestLogError', bannerType: 'error', children: <span>You have no permissions to use mySpareParts Analyzer. Try again or contact Customer Support at <a style={{ color: '#0057ff' }} href={`mailto:us-iasvsupport@abb.com?Subject=mySpareParts%20Analyzer%20Error`} target='_top'>us-iasvsupport@abb.com</a>.</span>}] });
      } else {
        dispatch({ type: 'ADD_BANNER', banners: [{id: 'getRequestLogError', bannerType: 'error', children: <span>Something went wrong and data could not be retrieved. Try again or contact Customer Support at <a style={{ color: '#0057ff' }} href={`mailto:us-iasvsupport@abb.com?Subject=mySpareParts%20Analyzer%20Error`} target='_top'>us-iasvsupport@abb.com</a>.</span>}] });
      }      
      console.error(`System error: ${error.status} ${error.statusText}`);
    }
  }

  render() {
    const SparePartColumn = ColumnDefinition as new () => ColumnDefinition<IMatchedSpareParts>;
    return (
      this.state.isContentLoaded ? 
      !this.state.requestDetails || this.state.requestDetails.statusName !== 'Approved' ?
      <p>Parts Report is not available for this request.</p> :
      (
        <div>
          <Waiting isOpen={this.state.waiting} />
          <div className='row '>
            <div className='col-lg-6'>
              <span>Request #{this.state.requestDetails.requestID}</span>
            </div>
            <div className='col-lg-3'/>
            <div className='col-lg-3'>
              <div className='row'>
                <div className='col-sm-8'/>
                <div className='col-sm-4'>
                  <h4>{this.getRequestStatus()}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-10'>
              <Cursor cursorSize={2} cursorClassName='cursorStyle'>
                Request Summary
              </Cursor>
            </div>
            <div className='col-sm-2' style={{ textAlign: 'right' }}>
              <div className='row'>
                <div className='col-sm-6'/>
                <div className='col-sm-4'>
                  <Button buttonType='normal' buttonSize='default' onClick={() => {this.setState({confirmationModelOpen: true}); }}>New Request</Button>
                </div>
              </div>
            </div>
          </div>
          <br/>
          <div className='row'>
            <div className='col-sm-4'>
              <h4>Parts Information</h4>
              <div className='row'>
                <div className='col-sm-6' style={{height: '180px', padding: '0px'}}>
                  <MySparePartsDonut matched={this.state.requestDetails.matchedParts.length}
                    unmatched={this.state.requestDetails.unMatchedParts.length}/>
                </div>
                <div className='col-sm-6'>
                  <br/>
                  <div>
                    <span>Submitted </span>
                    <Cooltip tooltipType='default' tooltipContents={<span>Unique parts identified.</span>} placement='topLeft' >
                        <Icon icon='information_circle2' color='#87d2ff' />
                    </Cooltip>
                    <p>{this.state.requestDetails.matchedParts.length + this.state.requestDetails.unMatchedParts.length}</p>
                  </div>
                  <br/>
                  <div>
                    <span>Matched </span>
                      <Cooltip tooltipType='default' tooltipContents={<span>Identified as ABB part numbers (exact matches only).</span>} placement='topLeft' >
                          <Icon icon='information_circle2' color='#87d2ff' />
                      </Cooltip>
                      <p>{this.state.requestDetails.matchedParts.length}</p>
                  </div>
                  <br/>
                  <div>
                    <span>Unmatched </span>
                      <Cooltip tooltipType='default' tooltipContents={<span>Unmatched parts could not be identified as ABB part numbers. If you feel this is not an accurate representation of your non-ABB parts inventory, please revisit your upload.</span>} placement='topLeft' >
                          <Icon icon='information_circle2' color='#87d2ff' />
                      </Cooltip>
                      <p>{this.state.requestDetails.unMatchedParts.length}</p>
                  </div>
                  <br/>
                  <div>
                    <p>Uploaded</p>
                    <p>{moment(this.state.requestDetails.uploaded).format('MM/DD/YYYY HH:mm:ss')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-sm-8'>
              <div className='row'>
                <h4>Survey Information</h4>
                <br/>
                <div className='col-sm-3' style={{padding: '0px'}}>
                  <span>Condition &amp; Location </span>
                  <Cooltip tooltipType='default' tooltipContents={<span>Do you know the condition and location of all of your spare parts?</span>} placement='topLeft' >
                      <Icon icon='information_circle2' color='#87d2ff' />
                  </Cooltip>
                  <p>{this.state.requestDetails.condition}</p>
                </div>
                <div className='col-sm-3'>
                  <div>
                    <span>Downtime </span>
                      <Cooltip tooltipType='default' tooltipContents={<span>Have you had downtime from missing or failed parts?</span>} placement='topLeft' >
                          <Icon icon='information_circle2' color='#87d2ff' />
                      </Cooltip>
                  </div>
                  <p>{this.state.requestDetails.downtime}</p>
                </div>
                <div className='col-sm-3'>
                  <div>
                    <span>Static &amp; Sealed Boxes </span>
                      <Cooltip tooltipType='default' tooltipContents={<span>Are your spare parts kept in sealed static bags and / or sealed boxes?</span>} placement='topLeft' >
                          <Icon icon='information_circle2' color='#87d2ff' />
                      </Cooltip>
                  </div>
                  <p>{this.state.requestDetails.storage}</p>
                </div>
                <div className='col-sm-3'>
                  <div>
                    <span>Program Familiarity </span>
                      <Cooltip tooltipType='default' tooltipContents={<span>Are you aware of all the options and programs ABB has available for spare parts?</span>} placement='topLeft' >
                          <Icon icon='information_circle2' color='#87d2ff' />
                      </Cooltip>
                  </div>
                  <p>{this.state.requestDetails.awareness}</p>
                </div>
              </div>
              <br/>
              <div className='row'>
                <h4>ABB Parts Specialist</h4>
                <br/>
                <div className='col-sm-6' style={{padding: '0px'}}>
                  <p>Contact Name</p>
                  <p>{this.state.requestDetails.partsSpecialistName ? this.state.requestDetails.partsSpecialistName : '-'}</p>
                </div>
                <div className='col-sm-6'>
                  <p>Email Address</p>
                  <p>{this.state.requestDetails.partsSpecialistEmail ? this.state.requestDetails.partsSpecialistEmail : '-'}</p>
                </div>
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col-xs-8'><h4>Spare Parts Analysis</h4></div>
            <div className='col-xs-4 text-right'>
              {this.state.requestDetails.statusName.toLowerCase() === 'approved'
                ? <Button className='blue-btn' buttonType='primary' buttonSize='default' onClick={this.downloadReport}>Download</Button>
                : null }
            </div>
          </div>
          <br/>
          <div className='row' style={{marginBottom: '20px'}}>
            <div className='container-fluid'>
              <Tab selected={0}>
                <TabPanel index={0} label={'Matched (' + this.state.requestDetails.matchedParts.length + ')'}>
                  <div className='row'>
                    <div className='col-lg-12'>
                      <Table data={this.state.requestDetails.matchedParts} fixedHeader={true}>
                        <SparePartColumn
                          cell={row => row.partNumber.toString()}
                          header='PART NUMBER'
                          columnName='partNumber'
                          columnWidth={1}
                        />
                        <SparePartColumn
                          cell={this.renderPartDescription}
                          header='ABB DESCRIPTION'
                          columnName='description'
                          columnWidth={2}
                        />
                        <SparePartColumn
                          cell={row => row.replacementPartNumber}
                          header='REPLACED BY'
                          columnName='replacementPart'
                          columnWidth={1}
                        />
                        <SparePartColumn
                          cell={row => row.quantity.toString()}
                          header='QUANTITY'
                          columnName='quantity'
                          columnWidth={1}
                        />
                        <SparePartColumn
                          cell={row => row.riskLevel === 'High' ? <div><span style={{whiteSpace: 'pre'}}>High      </span><span><Icon icon='warning_triangle' color='red' size='small' /></span></div> : row.riskLevel as string}
                          header={<div><span>RISK LEVEL </span> <Cooltip tooltipType='default' tooltipContents={<span>High/Medium/Low identifier relative to a business risk of not having part availability.</span>} placement='topLeft' ><Icon icon='information_circle2' color='#87d2ff' /></Cooltip></div>}
                          columnName='riskLevel'
                          columnWidth={1}
                        />
                        <SparePartColumn
                          cell={row => row.lifeCycle}
                          header={<div><span>LIFE CYCLE </span> <Cooltip tooltipType='default' tooltipContents={<span>Active/Classic/Limited/Obsolete identifier showing a state within the life-cycle of a part.</span>} placement='topLeft' ><Icon icon='information_circle2' color='#87d2ff' /></Cooltip></div>}
                          columnName='lifeCycle'
                          columnWidth={1}
                        />
                        <SparePartColumn
                          cell={row => row.countryStock}
                          header={<div><span>COUNTRY STOCK </span> <Cooltip tooltipType='default' tooltipContents={<span>Yes/No identifier validating if the part is in stock in an ABB warehouse in the country where the request was originated.</span>} placement='topLeft' ><Icon icon='information_circle2' color='#87d2ff' /></Cooltip></div>}
                          columnName='countryStock'
                          columnWidth={2}
                        />
                        <SparePartColumn
                          cell={row => row.replaceable}
                          header={<div><span>REPLACEABLE </span> <Cooltip tooltipType='default' tooltipContents={<span>Yes/No identifier validating if a part can be replaced.</span>} placement='topLeft' ><Icon icon='information_circle2' color='#87d2ff' /></Cooltip></div>}
                          columnName='replaceable'
                          columnWidth={2}
                        />
                        <SparePartColumn
                          cell={row => row.repairable}
                          header={<div><span>REPAIRABLE </span> <Cooltip tooltipType='default' tooltipContents={<span>Yes/No identifier validating if a part can be repaired.</span>} placement='topLeft' ><Icon icon='information_circle2' color='#87d2ff' /></Cooltip></div>}
                          columnName='repairable'
                          columnWidth={1}
                        />
                      </Table>
                    </div>
                  </div>
                </TabPanel>
                <TabPanel index={1} label={'Unmatched (' + this.state.requestDetails.unMatchedParts.length + ')'}>
                <div className='row'>
                    <div className='col-lg-12'>
                      <Table data={this.state.requestDetails.unMatchedParts} fixedHeader={true} >
                        <SparePartColumn
                          cell={row => row.partNumber.toString()}
                          header='PART NUMBER'
                          columnName='partNumber'
                          columnWidth={3}
                        />
                        <SparePartColumn
                          cell={this.renderPartDescription}
                          header='DESCRIPTION'
                          columnName='description'
                          columnWidth={7}
                        />
                        <SparePartColumn
                          cell={row => row.quantity.toString()}
                          header='QUANTITY'
                          columnName='quantity'
                          columnWidth={2}
                        />
                      </Table>
                    </div>
                  </div>
                </TabPanel>
              </Tab>
            </div>
          </div>
          <NewRequestConfirmationModel isOpen={this.state.confirmationModelOpen} onClose={this.onConfirmationModelClosed} onSubmit={this.onConfirmationModelSubmit} />
        </div>
      ) : ''
    );
  }

  onConfirmationModelClosed = () => {
    this.setState({ confirmationModelOpen: false });
  }

  onConfirmationModelSubmit = () => {
    this.props.history.push('/');
  }

  renderPartDescription = (detailedParts: IMatchedSpareParts) => {
    const overlay = (
      <div>
        {detailedParts.description}
      </div>
    );
    return (
      <Cooltip placement='bottom' tooltipContents={overlay} tooltipType='default'>
        <div className='wrapText'>
          {detailedParts.description}
        </div>
      </Cooltip>
    );
  }

  getRequestStatus() {
    return (this.state.requestDetails.statusName === 'Approved' ? <div><Icon icon='check_mark_circle2' size='small' color='green'/><span style={{whiteSpace: 'pre'}}>  Approved</span></div> : '');
  }

  downloadReport = async () => {
    this.setState({waiting: true}, async () => {
      try {
        await DownloadReport(this.state.requestDetails.sparePartsUploadDetailsID, this.state.requestDetails.sparePartsUploadID, this.state.requestDetails.requestID);
      } catch (error) {
        console.log(error);
      }
      this.setState({waiting: false});
    });
  }
}

export const CustomerReport = withRouter(connect(null)(CustomerReportComponent) as any);
